/**
 * Çıkarım kalitesi (provenance) analizi.
 *
 * Motorların kararlarını "metin ne kadar güvenilir?" sorusuna bağlar. Taranmış
 * PDF'ler, eksik glifler ve operatör kaybı burada tespit edilir; düşük kaliteli
 * belgelerde istatistik/atıf bulgularının güveni otomatik olarak düşürülür.
 */

import type { Confidence, ExtractedDocument, ExtractionQuality } from '../types';
import { findOperatorSuspects } from './normalize';

export interface QualityInput {
	text: string;
	chars: number;
	pages: number;
	emptyPages: number;
	operatorSuspects?: number;
	notes?: string[];
	format: 'pdf' | 'docx' | 'latex' | 'txt';
}

export function assessQuality(input: QualityInput): ExtractionQuality {
	const notes = [...(input.notes ?? [])];
	const chars = input.chars || input.text.length;

	// Çöp karakter oranı: değiştirme karakteri, kutu glifleri, kontrol karakterleri
	let garbage = 0;
	for (let i = 0; i < input.text.length; i++) {
		const c = input.text.charCodeAt(i);
		if (
			input.text[i] === '\uFFFD' ||
			input.text[i] === '\u25A1' ||
			input.text[i] === '\u25AF' ||
			(c > 0 && c < 9) ||
			(c >= 14 && c <= 31)
		) {
			garbage++;
		}
	}
	const garbageRatio = chars > 0 ? garbage / chars : 0;

	const operatorCorruptions =
		input.operatorSuspects ?? findOperatorSuspects(input.text).length;

	// Tek harf/kelime oranı düşükse ligatür veya sütun sorunu olabilir
	const singleCharRatio = estimateSingleCharRatio(input.text);

	let score = 1;

	if (input.format === 'pdf') {
		if (input.pages > 0) {
			const emptyRatio = input.emptyPages / input.pages;
			if (emptyRatio > 0.5) {
				score -= 0.45;
				notes.push(
					`${input.emptyPages}/${input.pages} sayfada metin katmanı yok (taranmış PDF olabilir; OCR gerekir).`
				);
			} else if (emptyRatio > 0) {
				score -= 0.15 * emptyRatio * 2;
				notes.push(`${input.emptyPages} sayfada metin katmanı bulunamadı.`);
			}
		}
		if (chars / Math.max(1, input.pages) < 180) {
			score -= 0.2;
			notes.push('Sayfa başına ortalama karakter sayısı düşük; metin eksik çıkarılmış olabilir.');
		}
	}

	if (garbageRatio > 0.01) {
		score -= Math.min(0.35, garbageRatio * 3);
		notes.push(
			`Metnin %${(garbageRatio * 100).toFixed(1)}'i tanınmayan glif içeriyor; kodlama/font sorunu olabilir.`
		);
	}

	if (operatorCorruptions > 0) {
		const penalty = Math.min(0.25, operatorCorruptions * 0.03);
		score -= penalty;
		notes.push(
			`${operatorCorruptions} istatistik ifadesinde operatör kaybı şüphesi var (örn. "p , .05").`
		);
	}

	if (singleCharRatio > 0.25 && chars > 400) {
		score -= 0.15;
		notes.push('Metinde kopuk tek karakterler yoğun; sütun/ligatür çıkarım sorunu olabilir.');
	}

	if (chars < 500) {
		score -= 0.3;
		notes.push('Belge çok kısa; analiz güvenilirliği sınırlı.');
	}

	score = clamp(score, 0, 1);
	const level: Confidence = score >= 0.75 ? 'high' : score >= 0.45 ? 'medium' : 'low';
	return {
		score: round(score, 3),
		level,
		chars,
		pages: input.pages,
		emptyPages: input.emptyPages,
		garbageRatio: round(garbageRatio, 4),
		operatorCorruptions,
		notes
	};
}

function estimateSingleCharRatio(text: string): number {
	const words = text.split(/\s+/).filter((w) => w.length > 0);
	if (words.length === 0) return 0;
	let single = 0;
	for (const w of words) if (w.length === 1 && !/[.,;:!?()\-\d]/.test(w)) single++;
	return single / words.length;
}

export function qualityToConfidence(q: ExtractionQuality): Confidence {
	return q.level;
}

/** Belge düzeyinde kaliteyi hesaplar. */
export function assessDocument(doc: ExtractedDocument): ExtractionQuality {
	const emptyPages = doc.blocks.filter((b) => b.page !== undefined).length
		? countEmptyPages(doc)
		: 0;
	return assessQuality({
		text: doc.text,
		chars: doc.text.length,
		pages: doc.pageCount,
		emptyPages,
		operatorSuspects: findOperatorSuspects(doc.text).length,
		notes: doc.warnings,
		format: doc.format
	});
}

function countEmptyPages(doc: ExtractedDocument): number {
	if (doc.pageCount === 0) return 0;
	const withText = new Set<number>();
	for (const b of doc.blocks) {
		if (b.page !== undefined && b.text.trim().length > 20) withText.add(b.page);
	}
	return Math.max(0, doc.pageCount - withText.size);
}

function clamp(v: number, lo: number, hi: number): number {
	return Math.max(lo, Math.min(hi, v));
}

function round(v: number, digits: number): number {
	const f = 10 ** digits;
	return Math.round(v * f) / f;
}
